import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Eye, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { ProspectForm } from "@/components/ProspectForm";
import { INDIVIDU_CATEGORIES, LEMBAGA_CATEGORIES, SOURCES, statusLabel, PIPELINE_STATUS_CLS } from "@/lib/prospectMeta";

const inputCls = "px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";

export default function Prospek() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ q: "", prospect_type: "", category: "", program: "", source: "", pic: "" });
  const pageSize = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, page_size: pageSize };
      const { data } = await api.get("/prospects", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) {
      toast.error("Gagal memuat prospek.");
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/prospects/meta").then(({ data }) => setMembers(data.members)).catch(() => {}); }, []);

  const setF = (k) => (e) => { setPage(1); setFilters((p) => ({ ...p, [k]: e.target.value, ...(k === "prospect_type" ? { category: "" } : {}) })); };
  const onSaved = () => { setShowForm(false); setEditing(null); load(); };
  const del = async (id, name) => {
    if (!window.confirm(`Hapus prospek "${name}"? (soft delete)`)) return;
    try { await api.delete(`/prospects/${id}`); toast.success("Prospek dihapus."); load(); }
    catch { toast.error("Gagal menghapus."); }
  };
  const catOptions = filters.prospect_type === "lembaga" ? LEMBAGA_CATEGORIES : filters.prospect_type === "individu" ? INDIVIDU_CATEGORIES : [...INDIVIDU_CATEGORIES, ...LEMBAGA_CATEGORIES];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-5">
        <div><h1 className="text-2xl font-bold text-slate-900">Prospek</h1><p className="text-sm text-slate-500">{total} prospek di cabang Anda</p></div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="prospek-add-button" className="inline-flex items-center gap-1.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium px-4 py-2.5 rounded-lg"><Plus className="h-4 w-4" /> Tambah Prospek</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className={`${inputCls} w-full pl-9`} placeholder="Cari nama, lembaga, WA, program…" value={filters.q} onChange={setF("q")} data-testid="prospect-search-input" />
        </div>
        <select className={inputCls} value={filters.prospect_type} onChange={setF("prospect_type")} data-testid="filter-type"><option value="">Semua Jenis</option><option value="individu">Individu</option><option value="lembaga">Lembaga</option></select>
        <select className={inputCls} value={filters.category} onChange={setF("category")} data-testid="filter-category"><option value="">Semua Kategori</option>{catOptions.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <input className={inputCls} placeholder="Program…" value={filters.program} onChange={setF("program")} data-testid="filter-program" />
        <select className={inputCls} value={filters.source} onChange={setF("source")} data-testid="filter-source"><option value="">Semua Sumber</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select className={inputCls} value={filters.pic} onChange={setF("pic")} data-testid="filter-pic"><option value="">Semua PIC</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="prospect-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">Nama</th><th className="text-left px-4 py-3">Jenis</th><th className="text-left px-4 py-3">Kategori</th><th className="text-left px-4 py-3">WhatsApp</th><th className="text-left px-4 py-3">Program</th><th className="text-left px-4 py-3">Sumber</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="prospect-empty">Belum ada prospek. Klik "Tambah Prospek".</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`prospect-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{p.institution_name || p.full_name}</td>
                  <td className="px-4 py-3">{p.prospect_type === "lembaga" ? "Lembaga" : "Individu"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.category || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.phone_normalized || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.program_interest || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{p.source || "-"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs border ${PIPELINE_STATUS_CLS[p.status] || PIPELINE_STATUS_CLS.prospek_baru}`}>{statusLabel(p.status)}</span>{p.converted && <span className="ml-1 px-2 py-0.5 rounded-full text-xs border bg-green-50 text-green-700 border-green-200" data-testid={`prospect-converted-${p.id}`}>Dikonversi</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => navigate(`/prospek/${p.id}`)} data-testid={`prospect-view-${p.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Detail / Follow-up"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`prospect-edit-${p.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Edit"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(p.id, p.institution_name || p.full_name)} data-testid={`prospect-delete-${p.id}`} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Hapus"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>Halaman {page} dari {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev" className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-next" className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {showForm && <ProspectForm mode={editing ? "edit" : "add"} initial={editing} members={members} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved} />}
    </AppLayout>
  );
}
